import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { AccountType } from "@front/m19-api-client";
import { BrandingFilter, ProductEx } from "@front/m19-models";
import { AccountSelectionService } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  IBadgeComponent,
  IButtonDirective,
  ICheckboxComponent,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  IInputDirective,
  IPaginationConfig,
  IPaginatorComponent,
  ISortSelectComponent,
  ITableBodyDirective,
  ITableDirective,
  ITableHeadDirective,
  ITableRowDirective,
  ITableThDirective,
  ITooltipDirective,
  ModalService,
  SortByConfig,
  SortingOption,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { LoadingService } from "@m19-board/services/loading.service";
import { CatalogProductEx, ManagedCatalogPageService } from "@m19-board/services/managed-catalog-page.service";
import {
  CompactFilterComponent,
  Filter,
  FilterCategory,
} from "@m19-board/shared/compact-filter/compact-filter.component";
import { AsinFilter, AsinFilterService } from "@m19-board/shared/filter/asin-filter.service";
import { map, Observable, of, switchMap, tap } from "rxjs";
import { CatalogRowComponent } from "../catalog-row/catalog-row.component";
import { CATALOG_DEFAULT_PAGE_SIZE, ProductTagField } from "../catalog.component";
import { CatalogToolbarComponent } from "./catalog-toolbar/catalog-toolbar.component";

@Component({
  selector: "app-managed-catalog-page",
  imports: [
    IBadgeComponent,
    ICheckboxComponent,
    CatalogRowComponent,
    ITableDirective,
    ITableBodyDirective,
    ITableHeadDirective,
    ITableRowDirective,
    ITableThDirective,
    CommonModule,
    IButtonDirective,
    ITooltipDirective,
    TranslocoDirective,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuTriggerDirective,
    IInputDirective,
    FormsModule,
    CatalogToolbarComponent,
    RouterLink,
    CompactFilterComponent,
    IPaginatorComponent,
    IIconComponent,
    ISortSelectComponent,
  ],
  templateUrl: "./managed-catalog-page.component.html",
  providers: [AsinFilterService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManagedCatalogPageComponent {
  private readonly managedCatalogPageService = inject(ManagedCatalogPageService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly translocoService = inject(TranslocoService);
  private readonly toastrService = inject(ToasterService);
  private readonly loadingService = inject(LoadingService);
  private readonly modalService = inject(ModalService);

  readonly ProductTagField = ProductTagField;
  readonly managedCatalog = this.managedCatalogPageService.managedCatalog;
  readonly inventoryConfig = this.managedCatalogPageService.inventoryConfig;
  readonly DEFAULT_PAGE_SIZE = CATALOG_DEFAULT_PAGE_SIZE;

  readonly currencyCode = input<string>();
  readonly currency = input<string>();
  readonly locale = input<string>();

  readonly sortByOptions: SortingOption<CatalogProductEx>[] = [
    {
      label: this.translocoService.translate("catalog-page.strategies_asc"),
      sortByField: "strategies",
      direction: "asc",
      compareFn: (a, b) => (a?.strategies?.length ?? 0) - (b?.strategies?.length ?? 0),
    },
    {
      label: this.translocoService.translate("catalog-page.strategies_desc"),
      sortByField: "strategies",
      direction: "desc",
      compareFn: (a, b) => (b?.strategies?.length ?? 0) - (a?.strategies?.length ?? 0),
    },
  ];
  readonly sortByConfig: SortByConfig<CatalogProductEx> = new SortByConfig(this.sortByOptions, this.sortByOptions[0]);

  filters = toSignal(this.managedCatalogPageService.filters$);
  selectedFilters = signal<Filter<AsinFilter>[]>([]);
  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$);
  readonly isVendor = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      map((am) => am.accountType !== AccountType.SELLER),
    ),
  );
  readonly productTag1Name = computed(
    () => this.inventoryConfig()?.customField1Name ?? this.translocoService.translate("common.product_tag_1"),
  );
  readonly productTag2Name = computed(
    () => this.inventoryConfig()?.customField2Name ?? this.translocoService.translate("common.product_tag_2"),
  );

  readonly currencySymbol = computed(() => this.currency() ?? "");
  pagination = signal<IPaginationConfig>({
    pageIndex: 0,
    pageSize: this.DEFAULT_PAGE_SIZE,
    length: 0,
  });

  readonly textFilter = signal<string>("");

  readonly selectedRows = this.managedCatalogPageService.selectedRows;

  readonly isAllSelected = computed(() => {
    const selectedRowsLength = this.selectedRows().size;
    const filteredCatalogLength = this.filteredCatalog()?.length ?? 0;
    return selectedRowsLength === filteredCatalogLength && selectedRowsLength > 0;
  });
  readonly selectedRowsCount = computed(() => this.selectedRows().size);

  readonly filteredCatalog = computed(() => {
    const filters = this.selectedFilters();
    const sortBy = this.sortByConfig.selectedSortBy();
    const filtered = this.managedCatalog()?.filter((product) => {
      const brandFilter = filters.find((filter) => filter.type === FilterCategory.BrandProducts);
      const productTag1Filter = filters.find((filter) => filter.type === FilterCategory.ProductTag1);
      const productTag2Filter = filters.find((filter) => filter.type === FilterCategory.ProductTag2);

      return (
        this.passesTextFilter(product) &&
        this.passesBrandFilter(product, brandFilter) &&
        this.passesProductTag1Filter(product, productTag1Filter) &&
        this.passesProductTag2Filter(product, productTag2Filter)
      );
    });
    if (sortBy?.compareFn) {
      filtered?.sort(sortBy.compareFn);
    }
    return filtered;
  });

  readonly catalogPaginated = computed(() =>
    this.filteredCatalog()?.slice(
      this.pagination().pageIndex * this.pagination().pageSize,
      (this.pagination().pageIndex + 1) * this.pagination().pageSize,
    ),
  );

  clearSelection() {
    this.selectedRows.set(new Set<string>());
  }

  onRowCheckboxClicked(asin: string) {
    this.selectedRows.update((selectedRows) => {
      if (selectedRows.has(asin)) {
        selectedRows.delete(asin);
      } else {
        selectedRows.add(asin);
      }
      return new Set([...selectedRows]);
    });
  }

  toggleSelectAllProducts() {
    if (this.isAllSelected()) {
      this.clearSelection();
    } else {
      this.selectedRows.set(new Set(this.filteredCatalog()?.map((product) => product.asin!) ?? []));
    }
  }

  groupEditProductTag(field: ProductTagField) {
    const asins = Array.from(this.selectedRows());
    const currentSelectedProductTags = this.managedCatalogPageService.getCurrentSelectedProductTags(asins, field);

    const result = this.managedCatalogPageService.openProductTagEditionModal(asins, field);

    result
      .pipe(
        tap((value: string) => {
          this.managedCatalogPageService.setProductTagsState(asins, field, value);
          this.clearSelection();
        }),
        switchMap((value) => {
          return this.managedCatalogPageService.updateProductTags(asins, field, value);
        }),
      )
      .subscribe({
        error: (error) => {
          this.toastrService.error(error.message);
          this.managedCatalogPageService.restoreProductTagsState(currentSelectedProductTags, field);
        },
      });
  }

  private promptForActiveStrategyStop(): Observable<boolean> {
    let hasActiveStrategies = false;
    for (const product of this.managedCatalog() ?? []) {
      if (this.selectedRows().has(product.asin!) && product.strategies && product.strategies.length > 0) {
        hasActiveStrategies = true;
        break;
      }
    }
    if (!hasActiveStrategies) {
      return of(true);
    }
    return this.modalService
      .openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
        data: {
          message:
            this.selectedRows().size === 1
              ? this.translocoService.translate("catalog-page.confirm_stop_managing_single_product_active_strategies")
              : this.translocoService.translate(
                  "catalog-page.confirm_stop_managing_multiple_products_active_strategies",
                ),
          color: "orange",
        },
      })
      .afterClosed();
  }

  updateProductsCatalog() {
    this.promptForActiveStrategyStop()
      .pipe(
        switchMap((confirmed) => {
          if (confirmed) {
            this.loadingService.loadingOn();
            return this.managedCatalogPageService.stopManagingProducts(Array.from(this.selectedRows()));
          }
          return of(null);
        }),
      )
      .subscribe({
        next: (result) => {
          if (result === null) {
            return;
          }
          this.toastrService.success(this.translocoService.translate("catalog-page.products_catalog_updated"));
          this.loadingService.loadingOff();
          this.clearSelection();
          this.textFilter.set("");
        },
        error: (err) => {
          this.toastrService.error(
            err,
            this.translocoService.translate("catalog-page.failed_to_remove_products_from_managed_catalog"),
          );
          this.loadingService.loadingOff();
        },
      });
  }

  openProductTagSettings() {
    this.managedCatalogPageService.openProductTagSettings();
  }

  uploadCatalogData() {
    this.managedCatalogPageService.uploadCatalogData(this.currencySymbol());
  }

  exportCatalog() {
    this.managedCatalogPageService.exportCatalog();
  }

  exportSeedsAsCsv() {
    this.managedCatalogPageService.exportSeedsAsCsv();
  }

  exportCogs() {
    this.managedCatalogPageService.exportCogs();
  }

  exportProductTags() {
    this.managedCatalogPageService.exportProductTags();
  }

  private passesTextFilter(product: ProductEx) {
    return (
      product.asin?.toLowerCase().includes(this.textFilter().toLowerCase()) ||
      product.title?.toLowerCase().includes(this.textFilter().toLowerCase()) ||
      product.brand?.toLowerCase().includes(this.textFilter().toLowerCase())
    );
  }

  private passesProductTag1Filter(product: ProductEx, filter: Filter<AsinFilter> | undefined) {
    if (!filter || filter.values.length === 0) {
      return true;
    }
    if (filter.type === FilterCategory.ProductTag1) {
      return filter.values.includes(product.productTag1!);
    }
    return true;
  }

  private passesProductTag2Filter(product: ProductEx, filter: Filter<AsinFilter> | undefined) {
    if (!filter || filter.values.length === 0) {
      return true;
    }
    if (filter.type === FilterCategory.ProductTag2) {
      return filter.values.includes(product.productTag2!);
    }
    return true;
  }

  private passesBrandFilter(product: ProductEx, filter: Filter<AsinFilter> | undefined) {
    if (!filter || filter.values.length === 0) {
      return true;
    }
    if (filter.type === FilterCategory.BrandProducts) {
      const brandAsins = new Set((filter.values as BrandingFilter[]).flatMap((b) => b.asins));
      return brandAsins.has(product.asin!);
    }
    return true;
  }
}
